import * as React from "react";
import classNames from "classnames";
import { useTranslation } from "./i18n";
import { winnerSuffix } from "./Trick";
var SEAT_ORDER = ["bottom", "left", "top", "right"];
var SEAT_CLASS = {
    bottom: "sj-seat-bottom",
    left: "sj-seat-left",
    top: "sj-seat-top",
    right: "sj-seat-right",
};
var ROLE_CLASS = {
    declarer: "is-team-declarer",
    teammate: "is-team-declarer",
    opponent: "is-team-opponent",
    unknown: "is-team-unknown",
};
/**
 * Determine a player's team role given the current game state.
 *
 * Pure presentation — does not change any game logic.
 */
var seatTeamRole = function (id, landlord, landlordsTeam, gameMode) {
    var _a;
    if (landlord !== undefined && landlord !== null && id === landlord) {
        return "declarer";
    }
    if ((_a = landlordsTeam === null || landlordsTeam === void 0 ? void 0 : landlordsTeam.includes(id)) !== null && _a !== void 0 ? _a : false) {
        return "teammate";
    }
    // Not on the landlord's team. In fixed-partnership Tractor this player is a
    // definitively-known opponent. In FindingFriends a player who isn't (yet) on
    // the landlord's team could be an unrevealed friend OR an opponent, so we
    // mark them as unknown until they're revealed.
    var isFindingFriends = gameMode !== undefined && gameMode !== "Tractor";
    return isFindingFriends ? "unknown" : "opponent";
};
var Seat = function (props) {
    var _a, _b, _c;
    var t = useTranslation().t;
    if (props.player === null) {
        return null;
    }
    var seatLabelKey = props.seat === "bottom"
        ? "play.seat.you"
        : props.seat === "left"
            ? "play.seat.left"
            : props.seat === "top"
                ? "play.seat.across"
                : "play.seat.right";
    var role = props.role;
    var roleTitleKey = role === "declarer"
        ? "team.declarerTitle"
        : role === "teammate"
            ? "team.teammateTitle"
            : role === "opponent"
                ? "team.opponentTitle"
                : "team.unknownTitle";
    // When a trick is in progress, every seat reserves a uniformly-sized play
    // slot (= the largest play this trick) so the compass stays balanced even if
    // one player throws a pair/tractor against three singles. The CSS variables
    // drive the slot's min-width / min-height (see .sj-seat-play in style.css).
    var hasTrick = props.uniform !== undefined;
    var slotStyle = props.uniform !== undefined
        ? {
            "--sj-play-cols": props.uniform.cols,
            "--sj-play-rows": props.uniform.rows,
        }
        : undefined;
    var playSlot = hasTrick ? (React.createElement("div", { className: "sj-seat-play", style: slotStyle }, (_a = props.play) === null || _a === void 0 ? void 0 :
        _a.node,
        (((_b = props.play) === null || _b === void 0 ? void 0 : _b.winning) === true || ((_c = props.play) === null || _c === void 0 ? void 0 : _c.better) === true) && (React.createElement("span", { className: "sj-seat-play-marker", "aria-hidden": "true" }, winnerSuffix(props.play.winning, props.play.better))))) : null;
    // Team membership is conveyed purely by the seat pill's COLOR (amber =
    // declarer's side, cyan = opponents, dashed slate = unrevealed) plus the 👑
    // on the declarer and a single "You" tag on the local player's own seat.
    // No redundant per-seat role text — that kept the felt cluttered.
    var pill = (React.createElement(React.Fragment, null,
        React.createElement("span", { className: classNames("sj-seat-name", ROLE_CLASS[role], {
                "is-self": props.isSelf,
                "is-turn": props.isTurn,
            }), title: "".concat(props.player.name, " \u00B7 ").concat(t(seatLabelKey), " \u00B7 ").concat(t(roleTitleKey)) },
            props.bot !== undefined && React.createElement("span", { "aria-hidden": "true" }, "\uD83E\uDD16"),
            React.createElement("span", { className: "sj-seat-name-text" }, props.player.name),
            role === "declarer" && (React.createElement("span", { "aria-hidden": "true", title: t("term.banker") }, "\uD83D\uDC51")),
            props.isSelf && (React.createElement("span", { className: "sj-seat-you-tag" }, t("team.you")))),
        props.bot !== undefined && (React.createElement("span", { className: "sj-seat-badge" }, t("ai.difficulty.".concat(props.bot.difficulty)))),
        React.createElement("span", { className: "sr-only" }, t(roleTitleKey)),
        props.isTurn && React.createElement("span", { className: "sr-only" }, t("rail.yourTurn"))));
    // Order the pill and the card so the pill sits on the OUTER edge of the table
    // and the cards reach toward the center — south reads pill-below-card, the
    // other three read pill-then-card (the per-seat CSS flips east/west axis).
    return (React.createElement("div", { className: classNames("sj-seat", SEAT_CLASS[props.seat], {
            "sj-seat--has-play": hasTrick,
        }) }, props.seat === "bottom" ? (React.createElement(React.Fragment, null,
        playSlot,
        pill)) : (React.createElement(React.Fragment, null,
        pill,
        playSlot))));
};
var Table = function (props) {
    var t = useTranslation().t;
    var botById = React.useMemo(function () {
        var _a;
        var m = {};
        ((_a = props.bots) !== null && _a !== void 0 ? _a : []).forEach(function (b) {
            m[b.player_id] = b;
        });
        return m;
    }, [props.bots]);
    // Rotate the player list so the local player sits at the bottom, then assign
    // the remaining players to left / top / right in seating order.
    var seatAssignments = React.useMemo(function () {
        var players = props.players;
        if (players.length === 0) {
            return [];
        }
        var startIdx = players.findIndex(function (p) { return p.id === props.selfId; });
        if (startIdx < 0) {
            startIdx = 0;
        }
        var rotated = [];
        for (var i = 0; i < players.length; i++) {
            rotated.push(players[(startIdx + i) % players.length]);
        }
        // Distribute up to 4 visible seats. For >4 players we only show the four
        // canonical seats; everyone is still listed in the player list above.
        return rotated.slice(0, 4).map(function (player, i) { return ({
            player: player,
            seat: SEAT_ORDER[i],
        }); });
    }, [props.players, props.selfId]);
    var roleOf = function (id) {
        return seatTeamRole(id, props.landlord, props.landlordsTeam, props.gameMode);
    };
    // A neutral allegiance only exists in FindingFriends.
    var hasUnknownSeats = seatAssignments.some(function (_a) {
        var player = _a.player;
        return roleOf(player.id) === "unknown";
    });
    var compass = props.compass;
    return (React.createElement("div", { className: "sj-table-wrap" },
        React.createElement("div", { className: classNames("sj-table", { "sj-table--compass": compass }), role: "group", "aria-label": "game table" },
            props.status !== undefined && (React.createElement("div", { className: "sj-table-status" }, props.status)),
            seatAssignments.map(function (_a) {
                var player = _a.player, seat = _a.seat;
                return (React.createElement(Seat, { key: player.id, player: player, seat: seat, role: roleOf(player.id), isSelf: player.id === props.selfId, isTurn: player.id === props.next, bot: botById[player.id], play: compass === null || compass === void 0 ? void 0 : compass.plays[player.id], uniform: compass === null || compass === void 0 ? void 0 : compass.uniform }));
            }),
            React.createElement("div", { className: "sj-center", "aria-hidden": compass !== undefined }, compass !== undefined ? (React.createElement("span", { className: "sj-compass-hub" })) : (props.center))),
        React.createElement("div", { className: "sj-team-legend", "aria-hidden": "true" },
            React.createElement("span", { className: "sj-team-legend-key" },
                React.createElement("span", { className: "sj-team-swatch sj-team-swatch-declarer" }),
                t("team.legend.declarerSide")),
            React.createElement("span", { className: "sj-team-legend-key" },
                React.createElement("span", { className: "sj-team-swatch sj-team-swatch-opponent" }),
                t("team.legend.opponents")),
            hasUnknownSeats && (React.createElement("span", { className: "sj-team-legend-key" },
                React.createElement("span", { className: "sj-team-swatch sj-team-swatch-unknown" }),
                t("team.legend.unknown"))))));
};
export default Table;
